import { Company, Expense, ExpenseKindEnum, ExpenseStatusEnum } from "@finway-group/shared/lib/models"
import { ExpensePaymentFlowInformationEnum } from "@finway-group/shared/lib/models/expense/expensePaymentFlowInformation.enum"
import { ExpensePaymentOptionEnum } from "@finway-group/shared/lib/models/expense/expensePaymentOption.enum"
import { Button, Dropdown, Menu, Modal } from "antd"
import React from "react"
import { Archive as ArchiveIcon, X as CancelIcon, Check as CheckedIcon, MoreHorizontal as MoreIcon, RotateCcw as RecoverIcon, RotateCw as ResetIcon } from "react-feather"
import { useTranslation } from "react-i18next"
import { useDispatch } from "react-redux"
import { useHistory } from "react-router"

import { archiveExpenses, cancelExpenseRequest, isExpenseArchived, markExpenseAsPaid, restoreExpense } from "Features/pages/expenses/expenseUtils"
import { getActiveWorkflowRule } from "Features/pages/workflows/workflow.utils"
import { useArchiveInterval } from "Shared/hooks/company.hooks"
import { useLoggedInEmployeeProfile } from "Shared/hooks/user.hooks"
import { useWorkflows } from "Shared/hooks/workflow.hooks"
import { NotificationService } from "Shared/services"
import DialogService from "Shared/services/dialog.service"
import { NotificationTypeEnum } from "Shared/services/notification.service"
import { resetExpense } from "Shared/store/actions/expense/expenseActions"
import { setLoading } from "Shared/store/actions/fetcher/fetcherActions"
import useStateIfMounted from "Shared/utils/hooks/useStateIfMounted"

interface AdditionalExpenseOptionButtonInterface {
    expense: Expense
    isEditDisabled: boolean
}

const AdditionalExpenseOptionButton: React.FC<AdditionalExpenseOptionButtonInterface> = ({ expense, isEditDisabled = false }) => {
    const { t } = useTranslation()
    const dispatch = useDispatch()
    const history = useHistory()
    const loggedInUser = useLoggedInEmployeeProfile()
    const [isLoading, setIsLoading] = useStateIfMounted(false)
    const deleteDisabled = expense.cannotBeDeleted() || (expense.isSuccessfullyProcessed() && !loggedInUser.activeCompanyProfile.isAdmin) || expense.deleted || isEditDisabled
    const workflows = useWorkflows()
    const archiveAfterXDays = useArchiveInterval()

    // resetting the status is mostly disabled when we have a payment flow status set

    const isExpenseMileageOrPerDiemAndInInvoicePendingState =
        (expense.kind === ExpenseKindEnum.MILEAGE || expense.kind === ExpenseKindEnum.PER_DIEM) && expense.status === ExpenseStatusEnum.INVOICE_PENDING

    const isResetDisabled =
        expense.paymentFlowInformation === ExpensePaymentFlowInformationEnum.REPORTING_ONLY ||
        (expense.isInReview() && expense.paymentFlowInformation === ExpensePaymentFlowInformationEnum.ALREADY_APPROVED_AND_PAID) ||
        isExpenseMileageOrPerDiemAndInInvoicePendingState

    const onSelect = ({ key, domEvent }: any) => {
        domEvent.stopPropagation()
        switch (key) {
            case "reset_status":
                onReset()
                break
            case "restore":
                onRestoreExpense()
                break
            case "delete":
                onDeleteExpense()
                break
            case "mark_as_paid":
                onMarkAsPaid()
                break
            case "archive":
                handleArchive()
                break
            default:
                break
        }
    }

    const onMarkAsPaid = () => {
        setIsLoading(true)
        markExpenseAsPaid(expense).finally(() => setIsLoading(false))
    }

    const onReset = async () => {
        const { activeRule, workflow } = getActiveWorkflowRule(expense, workflows)
        const isWorkflowReset = expense.isPending() && workflow && activeRule && activeRule.order !== 1

        if ((isWorkflowReset && (await DialogService.confirmExpenseWorkflowReset())) || (await DialogService.confirmExpenseStatusReset())) {
            try {
                setLoading(true)
                await resetExpense(expense.id)(dispatch)
                NotificationService.send(NotificationTypeEnum.SUCCESS, t("notification:request.reset.title"), t("notification:request.reset.message"))
            } catch (err) {
                NotificationService.showErrorNotificationBasedOnResponseError(err, t("error:error"))
            } finally {
                setIsLoading(false)
            }
        }
    }

    const onRestoreExpense = () => {
        Modal.confirm({
            title: t("confirm:request.restore.title"),
            content: t("confirm:request.restore.message"),
            cancelText: t("confirm:request.restore.cancel"),
            type: "warning",
            okText: t("confirm:request.restore.confirm"),
            async onOk() {
                try {
                    setLoading(true)
                    await restoreExpense(expense, loggedInUser.id)
                } catch (err) {
                    NotificationService.send(NotificationTypeEnum.ERROR, t("error:error"), err)
                } finally {
                    setIsLoading(false)
                }
            },
        })
    }

    const onDeleteExpense = () => {
        cancelExpenseRequest(expense, true, () => {
            history.goBack()
        })
    }

    const handleArchive = () => {
        setIsLoading(true)
        archiveExpenses([expense], loggedInUser, archiveAfterXDays).finally(() => setIsLoading(false))
    }

    const dropDownMenu = () => (
        <Menu onClick={onSelect}>
            {expense.deleted ? (
                <Menu.Item key="restore" className="flex items-center min-w-190 py-8">
                    <div className="flex items-center justify-center rounded-full w-16 h-16">
                        <RecoverIcon />
                    </div>
                    <div className="flex items-center ml-12">
                        <p className="leading-none">
                            <span className="text-text">{t("action:request.restore")}</span>
                        </p>
                    </div>
                </Menu.Item>
            ) : (
                <>
                    {expense.status === ExpenseStatusEnum.REVIEWED && expense.paymentOption === ExpensePaymentOptionEnum.SMART_CARD && (
                        <Menu.Item key="mark_as_paid" className="flex items-center min-w-190 py-8">
                            <div className="flex items-center justify-center rounded-full w-16 h-16">
                                <CheckedIcon />
                            </div>
                            <div className="flex items-center ml-12">
                                <p className="leading-none">
                                    <span className="text-text">{t("action:mark_as_paid")}</span>
                                </p>
                            </div>
                        </Menu.Item>
                    )}
                    <Menu.Item key="delete" className="flex items-center min-w-190 py-8" disabled={deleteDisabled}>
                        <div className="flex items-center justify-center rounded-full w-16 h-16">
                            <CancelIcon />
                        </div>
                        <div className="flex items-center ml-12">
                            <p className="leading-none">
                                <span className="text-text">{t("action:delete")}</span>
                            </p>
                        </div>
                    </Menu.Item>
                    <Menu.Item key="reset_status" className="flex items-center min-w-190 py-8" disabled={isResetDisabled}>
                        <div className="flex items-center justify-center rounded-full w-16 h-16">
                            <ResetIcon />
                        </div>
                        <div className="flex items-center ml-12">
                            <p className="leading-none">
                                <span className="text-text">{t("action:request.reset_status")}</span>
                            </p>
                        </div>
                    </Menu.Item>
                    <Menu.Item key="archive" className="flex items-center min-w-190 py-8">
                        <div className="flex items-center justify-center rounded-full w-16 h-16">
                            <ArchiveIcon />
                        </div>
                        <div className="flex items-center ml-12">
                            <p className="leading-none">
                                <span className="text-text">{isExpenseArchived(expense, archiveAfterXDays) ? t("action:archive.undo") : t("action:archive.do")}</span>
                            </p>
                        </div>
                    </Menu.Item>
                </>
            )}
        </Menu>
    )

    return (
        <Dropdown overlay={dropDownMenu} trigger={["click"]} className="flex items-center z-100 m-5">
            <Button loading={isLoading} onClick={(e: React.MouseEvent<HTMLElement>) => e.stopPropagation()}>
                <MoreIcon />
            </Button>
        </Dropdown>
    )
}

export default AdditionalExpenseOptionButton
